import type { ViewBody, ViewResolution } from '@ayumia/contracts';
import { coreComponents } from '@ayumia/contracts/components';
import { isCollectionRepresentation, representationsOf, type CoreRepresentation } from '@ayumia/component-runtime';

/**
 * Vue 3.0 vue du navigateur (ADR-099) : une vue est un assemblage de composants du registre Core.
 * Chaque renderer reçoit les entrées de son instance ; le conteneur `Collection` lit les contrôles
 * qu'une représentation prend en charge dans le manifeste de son composant, pas dans une table
 * codée ici. Rien de ce qui suit n'autorise : le serveur réautorise chaque lecture et commande.
 */

export type Representation = ViewBody['representations'][number];
export type TableInputs = CoreRepresentation<'Table'>['inputs'];
export type CardListInputs = CoreRepresentation<'CardList'>['inputs'];
export type KanbanInputs = CoreRepresentation<'Kanban'>['inputs'];
export type KanbanColumn = KanbanInputs['columns'][number];
export type AgendaInputs = CoreRepresentation<'Agenda'>['inputs'];
export type WeekGridInputs = CoreRepresentation<'WeekGrid'>['inputs'];
export type DetailInputs = CoreRepresentation<'Detail'>['inputs'];
export type FormInputs = CoreRepresentation<'Form'>['inputs'];
export type DashboardInputs = CoreRepresentation<'Dashboard'>['inputs'];
export type FieldRef = TableInputs['columns'][number];
export type CardSlot = NonNullable<CardListInputs['card']>;
export type CardZonesSlot = Extract<CardSlot, { zones: unknown }>;
export type EventBindings = NonNullable<Representation['events']>;

/** Zones de carte d'un emplacement (ADR-100) ; un composite publié n'en a pas (rendu par son manifeste). */
export function cardZonesOf(slot: CardSlot | undefined): CardZonesSlot | undefined {
  return slot !== undefined && 'zones' in slot ? slot : undefined;
}

export const detailOf = (view: ViewBody): DetailInputs | undefined => representationsOf(view, 'Detail')[0]?.inputs;
export const formOf = (view: ViewBody): FormInputs | undefined => representationsOf(view, 'Form')[0]?.inputs;
export const dashboardOf = (view: ViewBody): DashboardInputs | undefined => representationsOf(view, 'Dashboard')[0]?.inputs;

/** Représentations servies par le conteneur Collection, dans l'ordre du sélecteur. */
export function collectionRepresentations(view: ViewBody): Representation[] {
  return view.representations.filter((representation) => isCollectionRepresentation(representation.component));
}

/** Colonnes de la liste : celles de la première Table de la vue (tri, rendu des cellules, cartes sans modèle). */
export function listColumns(view: ViewBody): readonly FieldRef[] {
  return representationsOf(view, 'Table')[0]?.inputs.columns ?? [];
}

export type ContainerFeature = 'search' | 'quick_filters' | 'period' | 'filters' | 'sort' | 'export' | 'totals' | 'selection' | 'select_all';

/** Contrôles du conteneur que le composant d'une représentation déclare (`container_features` de son manifeste). */
export function containerFeatures(representation: Representation | undefined): ReadonlySet<ContainerFeature> {
  const declared = representation === undefined ? undefined : coreComponents[representation.component]?.container_features;
  return new Set((declared ?? []) as ContainerFeature[]);
}

/** Libellé et icône du sélecteur : le libellé de l'instance, sinon celui du composant au registre. */
export function representationLabel(representation: Representation): { label: string; icon: string } {
  const own = representation.label?.['fr'];
  const manifest = coreComponents[representation.component]?.component.label?.['fr'];
  const compact = representation.component === 'Table' && (representation.inputs as TableInputs).density === 'compact';
  const icon = representation.component === 'CardList' ? 'cards' : representation.component === 'Kanban' ? 'columns' : representation.component === 'Agenda' ? 'calendar' : representation.component === 'WeekGrid' ? 'grid' : compact ? 'rows' : 'table';
  return { label: own ?? manifest ?? representation.id, icon };
}

/** Éditeur de lignes d'un champ tableau de la charge utile d'une commande (ADR-136 §2, core-component-inputs 1.6.0). */
export type LineEditorInputs = NonNullable<NonNullable<ViewResolution['commands']>[number]['line_editor']>;

/**
 * L'éditeur de lignes d'une commande, tel que `GetView` le sert avec elle (view-resolution 2.3.0) : le
 * navigateur ne le devine jamais. Absent, la boîte de saisie de la charge utile reste la même.
 */
export function lineEditorOf(command: { line_editor?: LineEditorInputs }): LineEditorInputs | undefined {
  return command.line_editor;
}
